// Faza 6.2.23 (2026-05-03): split z customers.ts — see .plans/audit-customers-split.md
// Sesja 3.5 (2026-05-03): drawer.ts nie czyta/zapisuje pól __sbState — proxy usunięte.

import { $currentUser } from '../../stores/auth.js';
import type { CustomerRow, ApiError } from '../../types/dto.ts';
import { renderSellBaseCallActions } from '../mobile-call-bridge.ts';
// 0238: tagi klienta w szufladzie + wspólny formatter telefonu (display-only).
import { wierszTagowSzuflady } from '../customer-tags.ts';
import { formatujTelefon } from '../orders-shared.ts';
import { showConfirm, showPrompt } from '../sb-dialogs.ts';
import { formatLocalDateShortPL } from '../_datetime.ts';


// Lazy delegates (Faza 6.2.23 — cross-module deps)
function _initDrawerResize(this: any, ...args: any[]): any { return (((window.__sbExports && window.__sbExports._initDrawerResize) || window._initDrawerResize) as any).apply(this, args); }
function statusColorForLabel(this: any, ...args: any[]): any { return (((window.__sbExports && window.__sbExports.statusColorForLabel) || window.statusColorForLabel) as any).apply(this, args); }

// Cross-file delegates (Faza 6.2.23 — między customers/* files)
// AUDYT 2026-08-11: każda mutacja klienta z szuflady musi zgasić cache liczników
// i auto-cache listy — inaczej lista/sidebar pokazują stan sprzed zmiany do F5.
function _odswiezKlientowPoMutacji() { try { var f = (window as any).__sbOdswiezKlientow; if (typeof f === 'function') f(); } catch (_e) {} }


function openCustomerDrawer(customerId: string | number | null | undefined) {
    document.body.classList.add('customer-drawer-open');
    var drawer = document.getElementById('customer-drawer');
    if (drawer) { drawer.classList.add('open'); _initDrawerResize(drawer, 'sellbase-drawer-w-customer'); }
    var body = document.getElementById('customer-drawer-body');
    if (!body) return;
    body.innerHTML = '<div style="padding:60px;text-align:center;color:var(--color-text-muted)"><i data-lucide="loader-2" style="width:24px;height:24px;animation:spin 1s linear infinite"></i></div>';
    lucide.createIcons();
    // Update URL (skip if already matching)
    if (customerId && window.location.pathname !== '/customers/' + customerId) {
      history.pushState(null, '', '/customers/' + encodeURIComponent(customerId));
    }

    // Bind close
    var closeBtn = document.getElementById('customer-drawer-close');
    if (closeBtn && !(closeBtn as any)._cdBound) {
      (closeBtn as any)._cdBound = true;
      closeBtn.addEventListener('click', closeCustomerDrawer);
    }
    var overlay = document.getElementById('customer-drawer-overlay');
    if (overlay && !(overlay as any)._cdBound) {
      (overlay as any)._cdBound = true;
      overlay.addEventListener('click', closeCustomerDrawer);
    }

    if (customerId) {
      _loadCustomerDrawerContent(customerId, body);
    } else {
      _loadNewCustomerForm(body);
    }
}

function closeCustomerDrawer() {
    document.body.classList.remove('customer-drawer-open');
    var drawer = document.getElementById('customer-drawer');
    if (drawer) drawer.classList.remove('open');
    history.pushState(null, '', '/customers');
}

async function _loadCustomerDrawerContent(customerId: string | number, container: HTMLElement) {
    try {
      var customer: CustomerRow = await SellBaseAPI.getCustomer(customerId);
      if (!customer) { container.innerHTML = '<div style="padding:40px;text-align:center">Nie znaleziono klienta.</div>'; return; }

      var isCompany = customer.type === 'company';
      var customerName = isCompany ? (customer.company_name || '') : ((customer.first_name || '') + ' ' + (customer.last_name || '')).trim();
      // FIX 2026-08-16: JEDNO źródło kolorów/etykiet statusu — SellBaseData
      // (lokalny słownik rozjeżdżał się z listą: stały zielony vs niebieski)
      // + tStatus, żeby szuflada tłumaczyła się jak lista.
      var statusObj = SellBaseData.getCustomerStatusObj(customer.status || 'standard');
      var i18nMod = (window as any).__sbI18n;
      var statusLabel = i18nMod && i18nMod.tStatus ? i18nMod.tStatus('customer', statusObj.key, statusObj.label) : statusObj.label;
      var statusColor = statusObj.color || '#6b7280';

      // Update topbar
      var titleEl = document.getElementById('customer-drawer-title');
      if (titleEl) {
        titleEl.innerHTML =
          '<span style="font-weight:700;font-size:15px">' + escapeHtml(customerName || 'Klient') + '</span>' +
          ' <span class="status-badge" data-status-cat="customer" data-status-key="' + escapeAttr(statusObj.key || '') + '" style="font-size:10px;background:' + statusColor + '22;color:' + statusColor + ';border:1px solid ' + statusColor + '44">' +
            escapeHtml(statusLabel) +
          '</span>' +
          ' <span style="font-size:11px;color:var(--color-text-muted)">' + escapeHtml(isCompany ? 'Firma' : 'Osoba') + '</span>' +
          ' <button class="btn-sm" id="cd-merge-btn" style="margin-left:8px;font-size:10px"><i data-lucide="git-merge" style="width:11px;height:11px"></i> Scal</button>' +
          ' <button class="btn-sm" id="cd-blacklist-btn" style="margin-left:4px;font-size:10px;color:var(--color-error)"><i data-lucide="user-x" style="width:11px;height:11px"></i> Czarna lista</button>' +
          ' <button class="btn-sm" id="cd-delete-btn" style="margin-left:4px;font-size:10px;color:var(--color-error);border-color:var(--color-error)"><i data-lucide="trash-2" style="width:11px;height:11px"></i> Usuń</button>';
      }

      // Orders history
      var orders = Array.isArray(customer.orders_history) ? customer.orders_history : [];
      var totalOrders = customer.orders || orders.length;

      // 2026-08-16: kafle liczą się z DENORMU backendu (PLN po kursach NBP,
      // wszystkie marketplace'y) — front liczył z orders_history uciętej
      // LIMIT-em 200, więc dla dużych klientów suma/średnia/ostatnie kłamały,
      // a lista pokazywała inne liczby niż szuflada. Fallback per-waluta
      // (2026-06-10) zostaje TYLKO dla rekordów bez przeliczonych agregatów.
      // TYLKO last_order_at decyduje o denormie (review 0238): total>0 bywa też
      // w LIVE fallbacku backendu (surowa suma multi-currency bez NBP) — tam
      // formatowanie jako PLN wskrzeszałoby buga per-waluta z 2026-06-10.
      var maDenorm = !!(customer as any).last_order_at;
      var lastOrder = orders.length > 0 ? orders[0] : null;
      var lastOrderDate = (customer as any).last_order_at
        ? (formatLocalDateShortPL((customer as any).last_order_at) || '—')
        : (lastOrder ? (formatLocalDateShortPL(lastOrder.date) || '—') : '—');

      var sumaHtml: string;
      var sredniaHtml: string;
      if (maDenorm) {
        var totalPln = Number(customer.total || 0);
        sumaHtml = SellBaseData.formatCurrency(totalPln);
        sredniaHtml = SellBaseData.formatCurrency(totalOrders > 0 ? totalPln / totalOrders : 0);
      } else {
        var perCur: Record<string, { sum: number; count: number }> = {};
        orders.forEach(function(o: any) {
          var c = String(o.currency || 'PLN').toUpperCase();
          if (!perCur[c]) perCur[c] = { sum: 0, count: 0 };
          perCur[c].sum += parseFloat(String(o.amount)) || 0;
          perCur[c].count++;
        });
        var curKeys = Object.keys(perCur);
        var _fmtPerCurrency = function(kind: 'sum' | 'avg'): string {
          if (!curKeys.length) return SellBaseData.formatCurrency(0);
          var multi = curKeys.length > 1;
          return curKeys.map(function(c) {
            var v = kind === 'sum' ? perCur[c].sum : (perCur[c].sum / perCur[c].count);
            var line = SellBaseData.formatCurrency(v, c);
            return multi ? '<div style="font-size:14px;line-height:1.5">' + line + '</div>' : line;
          }).join('');
        };
        sumaHtml = _fmtPerCurrency('sum');
        sredniaHtml = _fmtPerCurrency('avg');
      }

      // KPI stats
      var statsHtml = '<div class="cd-stats-row">' +
        '<div class="cd-stat-card"><div class="cd-stat-label">Zamówienia</div><div class="cd-stat-value">' + totalOrders + '</div></div>' +
        '<div class="cd-stat-card"><div class="cd-stat-label">Łączna wartość</div><div class="cd-stat-value">' + sumaHtml + '</div></div>' +
        '<div class="cd-stat-card"><div class="cd-stat-label">Średnia wartość</div><div class="cd-stat-value">' + sredniaHtml + '</div></div>' +
        '<div class="cd-stat-card"><div class="cd-stat-label">Ostatnie zamówienie</div><div class="cd-stat-value" style="font-size:14px">' + lastOrderDate + '</div></div>' +
      '</div>';

      // 2026-06-10: layout 2-kolumnowy — Dane klienta (z adresem) | Dane do faktury
      // (sekcja faktury TYLKO gdy klient ma dane fakturowe; inaczej przycisk dodania).
      var html = statsHtml +
        '<div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px">' +
          _buildCustomerDataSection(customer) +
          _buildCustomerInvoiceSection(customer) +
        '</div>' +
        // 2026-06-10: adresy dostawy z historii zamowien — znacznik "taki sam jak
        // adres kupujacego" albo kafelki roznych adresow (gdy klient nadawal na rozne).
        _buildCustomerDeliveryAddresses(customer, orders) +
        // 2026-06-10: sprawy z klientem (reklamacje/zwroty/wiadomosci/wewnetrzne)
        '<div class="detail-section" id="cd-cases-section"><div class="detail-section-title"><i data-lucide="messages-square" style="width:16px;height:16px"></i> Sprawy z klientem</div><div id="cd-cases-body" style="font-size:12px;color:var(--color-text-muted);padding:8px 0">Ładowanie…</div></div>' +
        // Orders history + notes full width below
        _buildCustomerOrdersDetailed(orders) +
        _buildCustomerNotesSection(customer);

      container.innerHTML = html;
      lucide.createIcons();
      _bindCustomerDrawerEvents(customer);
      // 2026-06-10: dociagnij sprawy z klientem (async, po renderze szkieletu sekcji)
      _loadCustomerCases(customer.id);
    } catch (e: any) {
      container.innerHTML = '<div style="padding:40px;text-align:center;color:var(--color-error)">Błąd: ' + escapeHtml(e.message || '') + '</div>';
    }
}

function _buildCustomerDataSection(customer: CustomerRow, editMode?: boolean) {
    var isCompany = customer.type === 'company';
    if (editMode) {
      return _buildCustomerDataSectionEditable(customer);
    }
    var html = '<div class="detail-section" id="cd-data-section"><div class="detail-section-title"><i data-lucide="user" style="width:16px;height:16px"></i> Dane klienta <button class="btn-sm" id="cd-edit-data-btn" style="margin-left:auto;font-size:10px"><i data-lucide="pencil" style="width:11px;height:11px"></i> Edytuj</button></div>';

    if (isCompany) {
      html += '<div class="detail-info-row"><span class="detail-info-label">Firma</span><span class="detail-info-value">' + escapeHtml(customer.company_name || '') + '</span></div>';
      html += '<div class="detail-info-row"><span class="detail-info-label">NIP</span><span class="detail-info-value">' + escapeHtml(customer.nip || '') + '</span></div>';
    }
    html += '<div class="detail-info-row"><span class="detail-info-label">Imię i nazwisko</span><span class="detail-info-value">' + escapeHtml(((customer.first_name||'') + ' ' + (customer.last_name||'')).trim() || '—') + '</span></div>';
    html += '<div class="detail-info-row"><span class="detail-info-label">Email</span><span class="detail-info-value">' + escapeHtml(customer.email || '—') + '</span></div>';
    html += '<div class="detail-info-row"><span class="detail-info-label">Telefon</span><span class="detail-info-value sb-call-phone-value">' +
      // 2026-08-16: wyświetlanie przez wspólny formatter (surowy numer dalej
      // idzie do akcji Call/SMS — formatujemy TYLKO tekst).
      '<span>' + escapeHtml(customer.phone ? formatujTelefon(customer.phone) : '—') + '</span>' +
      renderSellBaseCallActions(customer.phone, { customerId: customer.id }) +
      '</span></div>';
    if (customer.login) html += '<div class="detail-info-row"><span class="detail-info-label">Login</span><span class="detail-info-value">' + escapeHtml(customer.login) + '</span></div>';
    html += '<div class="detail-info-row"><span class="detail-info-label">Status</span><span class="detail-info-value"><select class="form-select" id="cd-status-select" style="width:auto;padding:3px 8px;font-size:12px"><option value="standard"' + (customer.status==='standard'?' selected':'') + '>Standard</option><option value="staly"' + (customer.status==='staly'?' selected':'') + '>Stały klient</option><option value="vip"' + (customer.status==='vip'?' selected':'') + '>VIP</option><option value="hurt"' + (customer.status==='hurt'?' selected':'') + '>Hurt</option><option value="czarna_lista"' + (customer.status==='czarna_lista'?' selected':'') + '>Czarna lista</option></select></span></div>';
    // 0238: tagi klienta (chipy + „+ tag"); obsługa kliknięć w customer-tags.ts.
    html += wierszTagowSzuflady(customer);
    html += '<div class="detail-info-row"><span class="detail-info-label">Data rejestracji</span><span class="detail-info-value">' + (customer.created_at ? new Date(customer.created_at).toLocaleDateString('pl-PL') : '—') + '</span></div>';

    // 2026-06-10: adres klienta WEWNATRZ karty Dane klienta (wczesniej osobna
    // kolumna "Adres dostawy"). Wrapper z id cd-addr-delivery-section — handler
    // edycji adresu podmienia go in-place na formularz (outerHTML).
    html += '<div id="cd-addr-delivery-section" style="margin-top:10px;padding-top:8px;border-top:1px solid var(--color-border)">' +
      '<div style="display:flex;align-items:center;gap:6px;font-size:12px;font-weight:600;margin-bottom:4px">' +
        '<i data-lucide="map-pin" style="width:13px;height:13px"></i> Adres' +
        '<button class="btn-sm cd-edit-addr-btn" data-prefix="delivery" style="margin-left:auto;font-size:10px"><i data-lucide="pencil" style="width:11px;height:11px"></i> Edytuj</button>' +
      '</div>' +
      (customer.delivery_name ? '<div class="detail-info-row"><span class="detail-info-label">Odbiorca</span><span class="detail-info-value">' + escapeHtml(customer.delivery_name) + '</span></div>' : '') +
      (customer.delivery_company ? '<div class="detail-info-row"><span class="detail-info-label">Firma</span><span class="detail-info-value">' + escapeHtml(customer.delivery_company) + '</span></div>' : '') +
      '<div class="detail-info-row"><span class="detail-info-label">Ulica</span><span class="detail-info-value">' + escapeHtml(customer.delivery_street || '—') + '</span></div>' +
      '<div class="detail-info-row"><span class="detail-info-label">Kod i miasto</span><span class="detail-info-value">' + escapeHtml([customer.delivery_postal_code, customer.delivery_city].filter(Boolean).join(' ') || '—') + '</span></div>' +
      (customer.delivery_province ? '<div class="detail-info-row"><span class="detail-info-label">Województwo</span><span class="detail-info-value">' + escapeHtml(customer.delivery_province) + '</span></div>' : '') +
      '<div class="detail-info-row"><span class="detail-info-label">Kraj</span><span class="detail-info-value">' + escapeHtml(customer.delivery_country || 'PL') + '</span></div>' +
    '</div>';

    html += '</div>';
    return html;
}

// 2026-06-10: sekcja "Dane do faktury" — pokazywana TYLKO gdy klient ma jakiekolwiek
// dane fakturowe (z zamowien z zaznaczona faktura lub wpisane recznie). Gdy brak —
// dyskretna karta z przyciskiem dodania. Spojne z drawerem zamowienia (sekcja
// faktury warunkowa, NIP zawsze widoczny gdy dane sa).
function _buildCustomerInvoiceSection(customer: CustomerRow) {
    var hasInvoiceData = !!(
      customer.invoice_name || customer.invoice_company || customer.invoice_street ||
      customer.invoice_postal_code || customer.invoice_city || customer.invoice_nip
    );
    if (!hasInvoiceData) {
      return '<div class="detail-section" id="cd-addr-invoice-section" style="display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;border-style:dashed;min-height:120px">' +
        '<div style="font-size:12px;color:var(--color-text-muted);text-align:center">Klient nie podał danych do faktury<br>przy żadnym zamówieniu.</div>' +
        '<button class="btn-sm" id="cd-add-invoice-btn" style="font-size:11px"><i data-lucide="file-plus-2" style="width:12px;height:12px"></i> Dodaj dane do faktury</button>' +
      '</div>';
    }
    return _buildCustomerAddressSection(customer, 'invoice', 'Dane do faktury');
}

function _buildCustomerDataSectionEditable(customer: CustomerRow) {
    var isCompany = customer.type === 'company';
    var html = '<div class="detail-section" id="cd-data-section">' +
      '<div class="detail-section-title">' +
        '<i data-lucide="user" style="width:16px;height:16px"></i> Dane klienta — edycja' +
        '<button class="btn-sm" id="cd-edit-cancel-btn" style="margin-left:auto;font-size:10px">Anuluj</button>' +
        '<button class="btn-primary btn-sm" id="cd-edit-save-btn" style="margin-left:4px;font-size:10px"><i data-lucide="check" style="width:11px;height:11px"></i> Zapisz</button>' +
      '</div>';

    html += '<div class="form-group" style="margin-bottom:6px"><label style="font-size:11px;color:var(--color-text-muted)">Typ</label>' +
      '<select class="form-select" id="cd-edit-type" style="font-size:12px">' +
        '<option value="individual"' + (!isCompany?' selected':'') + '>Osoba prywatna</option>' +
        '<option value="company"' + (isCompany?' selected':'') + '>Firma</option>' +
      '</select></div>';
    html += '<div class="form-group" id="cd-edit-company-fields" style="margin-bottom:6px;display:' + (isCompany?'block':'none') + '">' +
      '<label style="font-size:11px;color:var(--color-text-muted)">Nazwa firmy</label><input class="form-input" id="cd-edit-company-name" value="' + escapeAttr(customer.company_name || '') + '" style="font-size:12px;margin-bottom:4px">' +
      '<label style="font-size:11px;color:var(--color-text-muted)">NIP</label><input class="form-input" id="cd-edit-nip" value="' + escapeAttr(customer.nip || '') + '" style="font-size:12px">' +
      '</div>';
    html += '<div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:6px">' +
      '<div><label style="font-size:11px;color:var(--color-text-muted)">Imię</label><input class="form-input" id="cd-edit-fname" value="' + escapeAttr(customer.first_name || '') + '" style="font-size:12px"></div>' +
      '<div><label style="font-size:11px;color:var(--color-text-muted)">Nazwisko</label><input class="form-input" id="cd-edit-lname" value="' + escapeAttr(customer.last_name || '') + '" style="font-size:12px"></div>' +
    '</div>';
    html += '<div class="form-group" style="margin-bottom:6px"><label style="font-size:11px;color:var(--color-text-muted)">Email</label><input class="form-input" id="cd-edit-email" type="email" value="' + escapeAttr(customer.email || '') + '" style="font-size:12px"></div>';
    html += '<div class="form-group" style="margin-bottom:6px"><label style="font-size:11px;color:var(--color-text-muted)">Telefon</label><input class="form-input" id="cd-edit-phone" type="tel" value="' + escapeAttr(customer.phone || '') + '" style="font-size:12px"></div>';
    html += '<div class="form-group" style="margin-bottom:6px"><label style="font-size:11px;color:var(--color-text-muted)">Status</label>' +
      '<select class="form-select" id="cd-edit-status" style="font-size:12px">' +
        '<option value="standard"' + (customer.status==='standard'?' selected':'') + '>Standard</option>' +
        '<option value="staly"' + (customer.status==='staly'?' selected':'') + '>Stały klient</option>' +
        '<option value="vip"' + (customer.status==='vip'?' selected':'') + '>VIP</option>' +
        '<option value="hurt"' + (customer.status==='hurt'?' selected':'') + '>Hurt</option>' +
        '<option value="czarna_lista"' + (customer.status==='czarna_lista'?' selected':'') + '>Czarna lista</option>' +
      '</select></div>';
    html += '</div>';
    return html;
}

function _buildCustomerAddressSection(customer: CustomerRow, prefix: string, title: string, editMode?: boolean) {
    if (editMode) return _buildCustomerAddressSectionEditable(customer, prefix, title);
    var street = customer[prefix + '_street'] || '';
    var postal = customer[prefix + '_postal_code'] || '';
    var city = customer[prefix + '_city'] || '';
    var province = customer[prefix + '_province'] || '';
    var country = customer[prefix + '_country'] || '';
    var name = customer[prefix + '_name'] || '';
    var company = customer[prefix + '_company'] || '';
    var nip = (prefix === 'invoice') ? (customer.invoice_nip || '') : '';
    var sectionId = 'cd-addr-' + prefix + '-section';

    var html = '<div class="detail-section" id="' + sectionId + '"><div class="detail-section-title">' +
      '<i data-lucide="map-pin" style="width:16px;height:16px"></i> ' + title +
      '<button class="btn-sm cd-edit-addr-btn" data-prefix="' + prefix + '" style="margin-left:auto;font-size:10px"><i data-lucide="pencil" style="width:11px;height:11px"></i> Edytuj</button>' +
      '</div>';
    if (name) html += '<div class="detail-info-row"><span class="detail-info-label">Odbiorca</span><span class="detail-info-value">' + escapeHtml(name) + '</span></div>';
    if (company) html += '<div class="detail-info-row"><span class="detail-info-label">Firma</span><span class="detail-info-value">' + escapeHtml(company) + '</span></div>';
    html += '<div class="detail-info-row"><span class="detail-info-label">Ulica</span><span class="detail-info-value">' + escapeHtml(street || '—') + '</span></div>';
    html += '<div class="detail-info-row"><span class="detail-info-label">Kod i miasto</span><span class="detail-info-value">' + escapeHtml([postal, city].filter(Boolean).join(' ') || '—') + '</span></div>';
    if (province) html += '<div class="detail-info-row"><span class="detail-info-label">Województwo</span><span class="detail-info-value">' + escapeHtml(province) + '</span></div>';
    html += '<div class="detail-info-row"><span class="detail-info-label">Kraj</span><span class="detail-info-value">' + escapeHtml(country || 'PL') + '</span></div>';
    if (prefix === 'invoice') {
      // NIP zawsze widoczny w sekcji faktury (— gdy brak) — user musi widziec ze jest miejsce
      html += '<div class="detail-info-row"><span class="detail-info-label">NIP</span><span class="detail-info-value"' + (nip ? ' style="font-weight:700;color:var(--color-primary)"' : '') + '>' + escapeHtml(nip || '—') + '</span></div>';
    }
    html += '</div>';
    return html;
}

function _buildCustomerAddressSectionEditable(customer: CustomerRow, prefix: string, title: string) {
    var street = customer[prefix + '_street'] || '';
    var postal = customer[prefix + '_postal_code'] || '';
    var city = customer[prefix + '_city'] || '';
    var province = customer[prefix + '_province'] || '';
    var country = customer[prefix + '_country'] || 'PL';
    var name = customer[prefix + '_name'] || '';
    var company = customer[prefix + '_company'] || '';
    var nip = (prefix === 'invoice') ? (customer.invoice_nip || '') : '';
    var sectionId = 'cd-addr-' + prefix + '-section';
    var p = prefix; // shorter

    var html = '<div class="detail-section" id="' + sectionId + '"><div class="detail-section-title">' +
      '<i data-lucide="map-pin" style="width:16px;height:16px"></i> ' + title + ' — edycja' +
      '<button class="btn-sm cd-edit-addr-cancel-btn" data-prefix="' + p + '" style="margin-left:auto;font-size:10px">Anuluj</button>' +
      '<button class="btn-primary btn-sm cd-edit-addr-save-btn" data-prefix="' + p + '" style="margin-left:4px;font-size:10px"><i data-lucide="check" style="width:11px;height:11px"></i> Zapisz</button>' +
      '</div>';
    html += '<div class="form-group" style="margin-bottom:6px"><label style="font-size:11px;color:var(--color-text-muted)">Odbiorca</label><input class="form-input cd-addr-input" data-field="' + p + '_name" value="' + escapeAttr(name) + '" style="font-size:12px"></div>';
    html += '<div class="form-group" style="margin-bottom:6px"><label style="font-size:11px;color:var(--color-text-muted)">Firma</label><input class="form-input cd-addr-input" data-field="' + p + '_company" value="' + escapeAttr(company) + '" style="font-size:12px"></div>';
    html += '<div class="form-group" style="margin-bottom:6px"><label style="font-size:11px;color:var(--color-text-muted)">Ulica i numer</label><input class="form-input cd-addr-input" data-field="' + p + '_street" value="' + escapeAttr(street) + '" style="font-size:12px"></div>';
    html += '<div style="display:grid;grid-template-columns:90px 1fr;gap:6px;margin-bottom:6px">' +
      '<div><label style="font-size:11px;color:var(--color-text-muted)">Kod</label><input class="form-input cd-addr-input" data-field="' + p + '_postal_code" value="' + escapeAttr(postal) + '" style="font-size:12px"></div>' +
      '<div><label style="font-size:11px;color:var(--color-text-muted)">Miasto</label><input class="form-input cd-addr-input" data-field="' + p + '_city" value="' + escapeAttr(city) + '" style="font-size:12px"></div>' +
    '</div>';
    html += '<div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:6px">' +
      '<div><label style="font-size:11px;color:var(--color-text-muted)">Województwo</label><input class="form-input cd-addr-input" data-field="' + p + '_province" value="' + escapeAttr(province) + '" style="font-size:12px"></div>' +
      '<div><label style="font-size:11px;color:var(--color-text-muted)">Kraj</label>' +
        '<select class="form-select cd-addr-input" data-field="' + p + '_country" style="font-size:12px">' +
          ['PL','DE','CZ','SK','HU','AT','FR','IT','ES','NL','BE','GB','US'].map(function(c){
            return '<option value="' + c + '"' + (country === c ? ' selected' : '') + '>' + c + '</option>';
          }).join('') +
        '</select>' +
      '</div>' +
    '</div>';
    if (prefix === 'invoice') {
      html += '<div class="form-group" style="margin-bottom:6px"><label style="font-size:11px;color:var(--color-text-muted)">NIP do faktury</label><input class="form-input cd-addr-input" data-field="invoice_nip" value="' + escapeAttr(nip) + '" style="font-size:12px"></div>';
    }
    html += '</div>';
    return html;
}

function _buildCustomerOrdersTimeline(orders: any) {
    var html = '<div class="detail-section"><div class="detail-section-title"><i data-lucide="clock" style="width:16px;height:16px"></i> Historia zamówień (' + orders.length + ')</div>';
    if (!orders.length) {
      html += '<div style="font-size:12px;color:var(--color-text-muted);padding:8px 0">Brak zamówień</div>';
    } else {
      html += '<div class="cd-timeline">';
      orders.slice(0, 20).forEach(function(o: any) {
        var statusColor = statusColorForLabel(o.status || 'Nowe');
        html += '<div class="cd-timeline-item" onclick="closeCustomerDrawer();SellBaseApp.navigate(\'/orders/' + escapeAttr(o.public_id || o.id) + '\')">' +
          '<div class="cd-timeline-date">' + (o.date ? new Date(o.date).toLocaleDateString('pl-PL') : '') + '</div>' +
          '<div class="cd-timeline-amount">' + SellBaseData.formatCurrency(parseFloat(o.amount) || 0, o.currency || 'PLN') + '</div>' +  // S-32 (E5): waluta zamówienia
          '<span class="cd-timeline-status status-badge ' + statusColor + '" style="font-size:10px">' + escapeHtml(o.status || '') + '</span>' +
          (o.products && o.products.length ? '<div style="font-size:10px;color:var(--color-text-muted);margin-top:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">' + escapeHtml(o.products.slice(0,2).join(', ')) + '</div>' : '') +
        '</div>';
      });
      html += '</div>';
    }
    html += '</div>';
    return html;
}

// 2026-06-10: adresy dostawy z historii zamowien. Porownujemy z adresem
// kupujacego (customer.delivery_*); gdy wszystkie zamowienia szly na ten sam
// adres = pojedynczy znacznik. Gdy klient nadawal na rozne adresy = kafelki.
function _normAddrKey(street: string, postal: string, city: string, country: string): string {
  return [street, postal, city, country].map(function(x){ return String(x||'').trim().toLowerCase().replace(/\s+/g,' '); }).join('|');
}
function _buildCustomerDeliveryAddresses(customer: CustomerRow, orders: any[]) {
  // Zbierz unikalne adresy dostawy z zamowien
  var map: Record<string, any> = {};
  (orders || []).forEach(function(o: any) {
    var street = o.delivery_street || '', postal = o.delivery_postal_code || '', city = o.delivery_city || '', country = o.delivery_country || '';
    if (!street && !city && !postal) return; // brak adresu w zamowieniu
    var key = _normAddrKey(street, postal, city, country);
    if (!map[key]) map[key] = { street: street, postal: postal, city: city, country: country, count: 0 };
    map[key].count++;
  });
  var addrs = Object.keys(map).map(function(k){ return map[k]; });

  var buyerKey = _normAddrKey(customer.delivery_street || '', customer.delivery_postal_code || '', customer.delivery_city || '', customer.delivery_country || 'PL');

  var html = '<div class="detail-section"><div class="detail-section-title"><i data-lucide="truck" style="width:16px;height:16px"></i> Adresy dostawy</div>';

  if (addrs.length === 0) {
    html += '<div style="font-size:12px;color:var(--color-text-muted);padding:6px 0">Brak danych adresowych w zamówieniach.</div>';
  } else if (addrs.length === 1 && _normAddrKey(addrs[0].street, addrs[0].postal, addrs[0].city, addrs[0].country) === buyerKey) {
    // Jedyny adres dostawy == adres kupujacego → znacznik
    html += '<div style="display:flex;align-items:center;gap:8px;font-size:12px;color:var(--color-success,#22c55e);padding:6px 0">' +
      '<i data-lucide="check-circle-2" style="width:15px;height:15px"></i>' +
      '<span>Adres dostawy taki sam jak adres kupującego</span>' +
    '</div>';
  } else {
    // Kafelki roznych adresow (sam adres + licznik zamowien)
    html += '<div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:8px">';
    addrs.sort(function(a,b){ return b.count - a.count; });
    addrs.forEach(function(a: any) {
      var sameAsBuyer = _normAddrKey(a.street, a.postal, a.city, a.country) === buyerKey;
      html += '<div style="border:1px solid var(--color-border);border-radius:8px;padding:8px 10px;font-size:12px;background:var(--color-surface)">' +
        '<div style="font-weight:600">' + escapeHtml(a.street || '—') + '</div>' +
        '<div style="color:var(--color-text-muted)">' + escapeHtml([a.postal, a.city].filter(Boolean).join(' ') || '') + (a.country && a.country !== 'PL' ? ' · ' + escapeHtml(a.country) : '') + '</div>' +
        '<div style="margin-top:4px;display:flex;align-items:center;gap:6px">' +
          '<span style="font-size:10px;color:var(--color-text-faint)">' + a.count + (a.count === 1 ? ' zamówienie' : (a.count < 5 ? ' zamówienia' : ' zamówień')) + '</span>' +
          (sameAsBuyer ? '<span style="font-size:10px;color:var(--color-success,#22c55e);display:inline-flex;align-items:center;gap:3px"><i data-lucide="user-check" style="width:11px;height:11px"></i>jak kupujący</span>' : '') +
        '</div>' +
      '</div>';
    });
    html += '</div>';
  }
  html += '</div>';
  return html;
}

// 2026-06-10: ladowanie + render "Spraw z klientem" (po renderze drawera).
async function _loadCustomerCases(customerId: string | number) {
  var body = document.getElementById('cd-cases-body');
  if (!body) return;
  var data: any;
  try {
    data = await (window as any).SellBaseAPI._request('GET', '/api/customers/' + encodeURIComponent(String(customerId)) + '/cases');
  } catch (e: any) {
    body.innerHTML = '<div style="color:var(--color-error);font-size:12px">Nie udało się pobrać spraw: ' + escapeHtml(e.message || '') + '</div>';
    return;
  }
  var items = (data && data.items) || [];
  if (!items.length) {
    body.innerHTML = '<div style="font-size:12px;color:var(--color-text-muted);padding:6px 0">Brak spraw z tym klientem.</div>';
    return;
  }
  var kindColor: Record<string,string> = {
    'aftersales_case': '#f59e0b', 'messaging_thread': '#3b82f6', 'internal_task': '#8b5cf6',
  };
  var html = '<div style="display:flex;flex-direction:column;gap:6px">';
  items.forEach(function(it: any, idx: number) {
    var col = kindColor[it.entity_type] || '#6b7280';
    var date = formatLocalDateShortPL(it.date);
    html += '<div class="cd-case-row" data-idx="' + idx + '" style="display:flex;align-items:center;gap:10px;border:1px solid var(--color-border);border-left:3px solid ' + col + ';border-radius:8px;padding:8px 12px;cursor:pointer" ' +
      'onmouseover="this.style.borderColor=\'var(--color-primary)\';this.style.borderLeftColor=\'' + col + '\'" onmouseout="this.style.borderColor=\'var(--color-border)\';this.style.borderLeftColor=\'' + col + '\'">' +
      '<span style="font-size:10px;font-weight:700;color:' + col + ';background:' + col + '18;border:1px solid ' + col + '30;border-radius:9999px;padding:1px 8px;white-space:nowrap">' + escapeHtml(it.kind_label || '') + '</span>' +
      '<div style="flex:1;min-width:0">' +
        '<div style="font-size:12px;font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">' + escapeHtml(it.subject || '') + '</div>' +
        (it.preview ? '<div style="font-size:11px;color:var(--color-text-muted);overflow:hidden;text-overflow:ellipsis;white-space:nowrap">' + escapeHtml(it.preview) + '</div>' : '') +
      '</div>' +
      (it.status ? '<span style="font-size:10px;color:var(--color-text-muted);white-space:nowrap">' + escapeHtml(it.status) + '</span>' : '') +
      '<span style="font-size:10px;color:var(--color-text-faint);white-space:nowrap">' + escapeHtml(date) + '</span>' +
    '</div>';
  });
  html += '</div>';
  body.innerHTML = html;
  try { (window as any).lucide && lucide.createIcons({ nodes: [body] }); } catch (_) {}
  body.querySelectorAll('.cd-case-row').forEach(function(row: any) {
    row.addEventListener('click', function() { _cdOpenCaseSource(items[parseInt(row.dataset.idx, 10)]); });
  });
}

// Deep-link do drawera zrodlowej sprawy (wzor: internal-tasks._itOpenSource).
async function _cdOpenCaseSource(it: any) {
  var w = window as any;
  var et = String(it.entity_type || '');
  var raw = String(it.entity_id || '');
  var m = raw.match(/^(\d+)\|(.+)$/);
  var accId = m ? parseInt(m[1], 10) : null;
  var plainId = m ? m[2] : raw;
  try {
    if (et === 'aftersales_case') {
      if (typeof w.openReturnDrawer !== 'function') { try { await import('../after-sales'); } catch (_) {} }
      if (typeof w.openReturnDrawer === 'function') { w.openReturnDrawer(plainId); return; }
      w.navigate && w.navigate('/returns'); return;
    }
    if (et === 'messaging_thread') {
      if (typeof w.openCustomerServiceMessagingDrawer !== 'function') { try { await import('../customer-service'); } catch (_) {} }
      if (accId && typeof w.openCustomerServiceMessagingDrawer === 'function') { w.openCustomerServiceMessagingDrawer(plainId, accId); return; }
      w.navigate && w.navigate('/tickets'); return;
    }
    if (et === 'internal_task') {
      if (typeof w.openInternalTaskDetail !== 'function') { try { await import('../internal-tasks'); } catch (_) {} }
      if (typeof w.openInternalTaskDetail === 'function') { w.openInternalTaskDetail(parseInt(plainId, 10)); return; }
      w.navigate && w.navigate('/tickets/mytasks'); return;
    }
  } catch (_) { /* noop */ }
}

function _buildCustomerOrdersDetailed(orders: any) {
    var html = '<div class="detail-section"><div class="detail-section-title"><i data-lucide="shopping-bag" style="width:16px;height:16px"></i> Historia zam\u00f3wie\u0144 (' + orders.length + ')</div>';
    if (!orders.length) {
      html += '<div style="padding:20px;text-align:center;color:var(--color-text-muted);font-size:12px">Brak zam\u00f3wie\u0144</div>';
    } else {
      // Summary
      // @ts-ignore TS6133 — zachowane dla parytetu z legacy (BATCH 3 strict migration)
      var totalAmt = orders.reduce(function(s: any, o: any) { return s + (parseFloat(o.amount) || 0); }, 0);
      var currencies = {};
      orders.forEach(function(o: any) { var c = o.currency || 'PLN'; (currencies as any)[c] = ((currencies as any)[c]||0) + (parseFloat(o.amount)||0); });

      orders.forEach(function(o: any) {
        var statusColor = statusColorForLabel(o.status || 'Nowe');
        var date = o.date ? new Date(o.date).toLocaleDateString('pl-PL') : '';
        var products = o.products || o.items || [];

        // 2026-06-10: klik → drawer zamowienia jako OVERLAY (stackuje sie NAD drawerem
        // klienta). showOrderDetail otwieral go "pod spodem"/nawigowal na /orders.
        html += '<div style="border:1px solid var(--color-border);border-radius:10px;padding:12px;margin-bottom:8px;cursor:pointer;transition:border-color .2s" onclick="(window.openOrderDrawerOverlay||window.SellBaseApp.showOrderDetail)(\'' + escapeAttr(o.id) + '\')" onmouseover="this.style.borderColor=\'var(--color-primary)\'" onmouseout="this.style.borderColor=\'var(--color-border)\'">';

        // Header row: date + status + amount
        html += '<div style="display:flex;align-items:center;gap:8px;margin-bottom:8px">' +
          '<span style="font-size:11px;color:var(--color-text-muted)">' + escapeHtml(date) + '</span>' +
          '<span class="status-badge ' + statusColor + '" style="font-size:10px">' + escapeHtml(o.status || '') + '</span>' +
          '<span style="margin-left:auto;font-size:14px;font-weight:700">' + SellBaseData.formatCurrency(parseFloat(o.amount) || 0, o.currency) + '</span>' +
        '</div>';

        // Product tiles
        if (Array.isArray(products) && products.length > 0) {
          html += '<div style="display:flex;flex-direction:column;gap:4px">';
          var displayProducts = typeof products[0] === 'string' ? products.map(function(p) { return {name: p}; }) : products;
          displayProducts.slice(0, 5).forEach(function(p) {
            var name = p.name || p.title || p.offerName || '';
            var qty = p.quantity || 1;
            var price = p.price || p.unit_price || 0;
            if (typeof price === 'object') price = price.amount || 0;
            html += '<div style="display:flex;align-items:center;gap:8px;padding:6px 8px;border-radius:6px;background:var(--color-surface-2);font-size:11px">' +
              (p.image_url || p.imageUrl ? '<img class="sb-thumb sb-zoom" src="' + escapeAttr(p.image_url || p.imageUrl) + '" style="width:32px;height:32px;border-radius:4px;object-fit:cover;flex-shrink:0" onerror="this.style.display=\'none\'">' : '<div style="width:32px;height:32px;border-radius:4px;background:var(--color-surface);display:flex;align-items:center;justify-content:center;flex-shrink:0"><i data-lucide="package" style="width:14px;height:14px;color:var(--color-text-muted)"></i></div>') +
              '<span style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">' + escapeHtml(name) + '</span>' +
              (qty > 1 ? '<span style="color:var(--color-text-muted)">' + qty + ' szt</span>' : '') +
              // 2026-06-10: cena pozycji w WALUCIE ZAMOWIENIA — bylo na sztywno PLN
              // (zamowienie 4560 HUF pokazywalo pozycje "3 770 PLN")
              (parseFloat(price) > 0 ? '<span style="font-weight:600">' + SellBaseData.formatCurrency(parseFloat(price), o.currency) + '</span>' : '') +
            '</div>';
          });
          if (displayProducts.length > 5) {
            html += '<div style="font-size:10px;color:var(--color-text-muted);padding:4px 8px">+ ' + (displayProducts.length - 5) + ' wi\u0119cej...</div>';
          }
          html += '</div>';
        }

        html += '</div>';
      });
    }
    html += '</div>';
    return html;
}

function _buildCustomerNotesSection(customer: CustomerRow) {
    // @ts-ignore TS6133 — zachowane dla parytetu z legacy (BATCH 3 strict migration)
    var notes = Array.isArray(customer._notes) ? customer._notes : [];
    var html = '<div class="detail-section"><div class="detail-section-title"><i data-lucide="message-square" style="width:16px;height:16px"></i> Notatki</div>';

    if (customer.notes) {
      html += '<div style="padding:8px 12px;border-radius:8px;background:var(--color-surface-2);font-size:12px;color:var(--color-text);margin-bottom:8px;white-space:pre-wrap">' + escapeHtml(customer.notes) + '</div>';
    }

    html += '<div style="display:flex;gap:6px;align-items:flex-start">' +
      '<textarea class="form-input" id="cd-note-text" rows="2" placeholder="Dodaj notatkę..." style="flex:1;font-size:12px;resize:vertical"></textarea>' +
      '<button class="btn-primary btn-sm" id="cd-add-note-btn" style="flex-shrink:0;padding:8px 12px"><i data-lucide="send" style="width:12px;height:12px"></i></button>' +
    '</div>';

    html += '</div>';
    return html;
}

function _loadNewCustomerForm(container: HTMLElement) {
    var titleEl = document.getElementById('customer-drawer-title');
    if (titleEl) titleEl.innerHTML = '<span style="font-weight:700;font-size:15px">Nowy klient</span>';

    // Type toggle
    var html = '<div class="cd-type-toggle">' +
      '<button class="cd-type-btn active" data-ctype="individual">👤 Osoba prywatna</button>' +
      '<button class="cd-type-btn" data-ctype="company">🏢 Firma</button>' +
    '</div>';

    // Form
    html += '<div class="cd-two-col"><div class="cd-col-left">';

    // Data section
    html += '<div class="detail-section"><div class="detail-section-title">Dane klienta</div>' +
      '<div id="cd-company-fields" style="display:none">' +
        '<div class="form-group" style="margin-bottom:8px"><label style="font-size:12px;color:var(--color-text-muted)">Nazwa firmy *</label><input class="form-input" id="cd-company-name"></div>' +
        '<div class="form-group" style="margin-bottom:8px"><label style="font-size:12px;color:var(--color-text-muted)">NIP</label><div style="position:relative"><input class="form-input" id="cd-nip" placeholder="10 cyfr — dane pobiorą się z GUS" inputmode="numeric"></div></div>' +
      '</div>' +
      '<div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:8px">' +
        '<div class="form-group"><label style="font-size:12px;color:var(--color-text-muted)">Imię *</label><input class="form-input" id="cd-fname"></div>' +
        '<div class="form-group"><label style="font-size:12px;color:var(--color-text-muted)">Nazwisko *</label><input class="form-input" id="cd-lname"></div>' +
      '</div>' +
      '<div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:8px">' +
        '<div class="form-group"><label style="font-size:12px;color:var(--color-text-muted)">Email *</label><input class="form-input" id="cd-email" type="email"></div>' +
        '<div class="form-group"><label style="font-size:12px;color:var(--color-text-muted)">Telefon</label><input class="form-input" id="cd-phone" type="tel"></div>' +
      '</div>' +
      '<div class="form-group" style="margin-bottom:8px"><label style="font-size:12px;color:var(--color-text-muted)">Status</label>' +
        '<select class="form-select" id="cd-status"><option value="standard">Standard</option><option value="staly">Stały klient</option><option value="vip">VIP</option><option value="hurt">Hurt</option><option value="czarna_lista">Czarna lista</option></select>' +
      '</div>' +
    '</div>';

    // Delivery address
    html += '<div class="detail-section"><div class="detail-section-title">Adres dostawy</div>' +
      '<input class="form-input" id="cd-del-street" placeholder="Ulica i numer" style="margin-bottom:6px">' +
      '<div style="display:grid;grid-template-columns:100px 1fr;gap:6px;margin-bottom:6px">' +
        '<input class="form-input" id="cd-del-zip" placeholder="Kod">' +
        '<input class="form-input" id="cd-del-city" placeholder="Miasto">' +
      '</div>' +
      '<div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:6px">' +
        '<input class="form-input" id="cd-del-province" placeholder="Województwo">' +
        '<select class="form-select" id="cd-del-country"><option value="PL">Polska</option><option value="DE">Niemcy</option><option value="CZ">Czechy</option><option value="SK">Słowacja</option></select>' +
      '</div>' +
    '</div>';

    html += '</div><div class="cd-col-right">';

    // Invoice address
    html += '<div class="detail-section"><div class="detail-section-title">Adres do faktury</div>' +
      '<label style="display:flex;align-items:center;gap:6px;margin-bottom:10px;font-size:12px;cursor:pointer"><input type="checkbox" id="cd-same-addr" checked> Taki sam jak dostawy</label>' +
      '<div id="cd-invoice-fields" style="display:none">' +
        '<input class="form-input" id="cd-inv-street" placeholder="Ulica i numer" style="margin-bottom:6px">' +
        '<div style="display:grid;grid-template-columns:100px 1fr;gap:6px;margin-bottom:6px">' +
          '<input class="form-input" id="cd-inv-zip" placeholder="Kod">' +
          '<input class="form-input" id="cd-inv-city" placeholder="Miasto">' +
        '</div>' +
        '<div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:6px">' +
          '<input class="form-input" id="cd-inv-province" placeholder="Województwo">' +
          '<select class="form-select" id="cd-inv-country"><option value="PL">Polska</option><option value="DE">Niemcy</option></select>' +
        '</div>' +
        '<input class="form-input" id="cd-inv-nip" placeholder="NIP do faktury" style="margin-bottom:6px">' +
      '</div>' +
    '</div>';

    // Notes
    html += '<div class="detail-section"><div class="detail-section-title">Notatki</div>' +
      '<textarea class="form-input" id="cd-notes" rows="4" placeholder="Notatki wewnętrzne..."></textarea>' +
    '</div>';

    html += '</div></div>';

    // Save bar
    html += '<div style="display:flex;justify-content:flex-end;margin-top:16px;padding:16px 20px;background:var(--color-surface);border:1px solid var(--color-border);border-radius:12px">' +
      '<button class="btn-primary" id="cd-save-btn" style="padding:10px 24px"><i data-lucide="check" style="width:16px;height:16px"></i> Utwórz klienta</button>' +
    '</div>';

    container.innerHTML = html;
    lucide.createIcons();

    // 2026-09-03 (user: „Przy dodawaniu nowego klienta też niech będzie od razu pobieranie
    // danych z GUS po wpisaniu NIP aby uzupełniło wszystko automatycznie co się da"):
    // lupa w polu NIP + automat po 10 cyfrach. Rejestr MF daje nazwę, adres (siedziby lub
    // zamieszkania dla JDG) i status VAT — wypełniamy nazwę (nadpisujemy), adres dostawy
    // z krajem PL oraz adres do faktury (przydaje się po odznaczeniu „Taki sam jak dostawy").
    // Pola już wpisane ręcznie (poza nazwą) zostają. Województwa rejestr nie zwraca.
    var _nipBtn = (window as any).__sbExports?._nipInitInlineButton || (window as any)._nipInitInlineButton;
    var _nipAuto = (window as any).__sbExports?._nipInitAutoLookup || (window as any)._nipInitAutoLookup;
    if (typeof _nipBtn === 'function') {
      _nipBtn('cd-nip', function(data: any) {
        var set = function(id: string, val: any, nadpisz?: boolean) {
          var el = document.getElementById(id) as any;
          if (!el || val == null || !String(val).trim()) return;
          if (nadpisz || !String(el.value || '').trim()) el.value = String(val).trim();
        };
        var kraj = function(id: string) {
          var el = document.getElementById(id) as any;
          if (!el) return;
          for (var i = 0; i < (el.options || []).length; i++) { if (el.options[i].value === 'PL') { el.selectedIndex = i; break; } }
        };
        var adr = (data && data.address && typeof data.address === 'object') ? data.address : {};
        var nipCyfry = String(data.nip || (document.getElementById('cd-nip') as any)?.value || '').replace(/\D/g, '');
        set('cd-company-name', data.name, true);
        set('cd-del-street', adr.street); set('cd-del-zip', adr.postal_code || adr.zip); set('cd-del-city', adr.city);
        if (adr.street || adr.city) kraj('cd-del-country');
        set('cd-inv-street', adr.street); set('cd-inv-zip', adr.postal_code || adr.zip); set('cd-inv-city', adr.city);
        if (adr.street || adr.city) kraj('cd-inv-country');
        set('cd-inv-nip', nipCyfry);
      });
      if (typeof _nipAuto === 'function') _nipAuto('cd-nip');
    }

    // Bind type toggle
    var _cdType = 'individual';
    container.querySelectorAll('.cd-type-btn').forEach(function(btn: any) {
      btn.addEventListener('click', function(this: any) {
        _cdType = this.dataset.ctype;
        container.querySelectorAll('.cd-type-btn').forEach(function(this: any, b: any) { b.classList.remove('active'); });
        this.classList.add('active');
        (document.getElementById('cd-company-fields') as any).style.display = _cdType === 'company' ? '' : 'none';
      });
    });

    // Bind same address checkbox
    var sameAddr = document.getElementById('cd-same-addr');
    if (sameAddr) sameAddr.addEventListener('change', function() {
      (document.getElementById('cd-invoice-fields') as any).style.display = (this as any).checked ? 'none' : '';
    });

    // Bind save
    var saveBtn: any = document.getElementById('cd-save-btn');
    if (saveBtn) saveBtn.addEventListener('click', async function() {
      var payload = { type: _cdType };
      if (_cdType === 'company') {
        (payload as any).company_name = ((document.getElementById('cd-company-name') as any).value || '').trim();
        (payload as any).nip = ((document.getElementById('cd-nip') as any).value || '').trim();
        if (!(payload as any).company_name) { showToast('Podaj nazwę firmy', 'error'); return; }
      }
      (payload as any).first_name = ((document.getElementById('cd-fname') as any).value || '').trim();
      (payload as any).last_name = ((document.getElementById('cd-lname') as any).value || '').trim();
      (payload as any).email = ((document.getElementById('cd-email') as any).value || '').trim();
      (payload as any).phone = ((document.getElementById('cd-phone') as any).value || '').trim();
      (payload as any).status = ((document.getElementById('cd-status') as any).value || 'standard');
      (payload as any).notes = ((document.getElementById('cd-notes') as any).value || '').trim();

      if (_cdType === 'individual' && (!(payload as any).first_name || !(payload as any).last_name)) { showToast('Podaj imię i nazwisko', 'error'); return; }
      if (!(payload as any).email) { showToast('Podaj email', 'error'); return; }

      // Delivery address
      (payload as any).delivery_street = ((document.getElementById('cd-del-street') as any).value || '').trim();
      (payload as any).delivery_postal_code = ((document.getElementById('cd-del-zip') as any).value || '').trim();
      (payload as any).delivery_city = ((document.getElementById('cd-del-city') as any).value || '').trim();
      (payload as any).delivery_province = ((document.getElementById('cd-del-province') as any).value || '').trim();
      (payload as any).delivery_country = ((document.getElementById('cd-del-country') as any).value || 'PL');

      // Invoice address
      if ((document.getElementById('cd-same-addr') as any).checked) {
        (payload as any).invoice_street = (payload as any).delivery_street;
        (payload as any).invoice_postal_code = (payload as any).delivery_postal_code;
        (payload as any).invoice_city = (payload as any).delivery_city;
        (payload as any).invoice_province = (payload as any).delivery_province;
        (payload as any).invoice_country = (payload as any).delivery_country;
      } else {
        (payload as any).invoice_street = ((document.getElementById('cd-inv-street') as any).value || '').trim();
        (payload as any).invoice_postal_code = ((document.getElementById('cd-inv-zip') as any).value || '').trim();
        (payload as any).invoice_city = ((document.getElementById('cd-inv-city') as any).value || '').trim();
        (payload as any).invoice_province = ((document.getElementById('cd-inv-province') as any).value || '').trim();
        (payload as any).invoice_country = ((document.getElementById('cd-inv-country') as any).value || 'PL');
        (payload as any).invoice_nip = ((document.getElementById('cd-inv-nip') as any).value || '').trim();
      }

      (saveBtn as any).disabled = true;
      saveBtn.textContent = 'Zapisuję...';
      try {
        await SellBaseAPI.createCustomer(payload);
        showToast('Klient utworzony', 'success');
        closeCustomerDrawer();
        _odswiezKlientowPoMutacji();
      } catch (e: any) {
        showToast('Błąd: ' + (e.message || ''), 'error');
        (saveBtn as any).disabled = false;
        saveBtn.innerHTML = '<i data-lucide="check" style="width:16px;height:16px"></i> Utwórz klienta';
        lucide.createIcons({nodes:[saveBtn]});
      }
    });
}

function _bindCustomerDrawerEvents(customer: CustomerRow) {
    // Status change (inline select w widoku)
    var statusSel = document.getElementById('cd-status-select');
    if (statusSel) {
      statusSel.addEventListener('change', async function() {
        try {
          await SellBaseAPI.updateCustomer(customer.id, { status: (this as any).value });
          showToast('Status zmieniony', 'success');
          _odswiezKlientowPoMutacji();
        } catch (e: any) { showToast('Błąd: ' + e.message, 'error'); }
      });
    }

    // Edit mode toggle: replace data section z formularzem + Save/Anuluj
    var editBtn = document.getElementById('cd-edit-data-btn');
    if (editBtn) {
      editBtn.addEventListener('click', function() {
        var section = document.getElementById('cd-data-section');
        if (!section) return;
        section.outerHTML = _buildCustomerDataSectionEditable(customer);
        if (window.lucide) lucide.createIcons();
        _bindEditDataFormEvents(customer);
      });
    }

    // Add note
    var noteBtn = document.getElementById('cd-add-note-btn');
    if (noteBtn) {
      noteBtn.addEventListener('click', async function() {
        var textarea = document.getElementById('cd-note-text');
        var text = textarea ? (textarea as any).value.trim() : '';
        if (!text) return;
        try {
          var existingNotes = customer.notes || '';
          var _u = $currentUser.get(); var _userName = _u ? ((_u.first_name || '') + ' ' + (_u.last_name || '')).trim() || 'Admin' : 'Admin';
          var newNotes = existingNotes + (existingNotes ? '\n' : '') + '[' + new Date().toLocaleString('pl-PL') + ' ' + _userName + '] ' + text;
          await SellBaseAPI.updateCustomer(customer.id, { notes: newNotes });
          showToast('Notatka dodana', 'success');
          _odswiezKlientowPoMutacji();
          _loadCustomerDrawerContent(customer.id, document.getElementById('customer-drawer-body')!);
        } catch (e: any) { showToast('Błąd: ' + e.message, 'error'); }
      });
    }

    // Merge button
    // 2026-08-26 (user): czarna lista — wpis z identyfikatorów klienta
    // (telefon/e-mail/login) + status klienta 'czarna_lista'.
    var blCustBtn = document.getElementById('cd-blacklist-btn');
    if (blCustBtn) {
      blCustBtn.addEventListener('click', function() {
        // 2026-09-03: modal z profilem, identyfikatorami do odznaczenia i powodem
        // (opcjonalnym) zamiast natywnego confirm(); po zapisie karta odświeża status.
        import('./blacklist-add-modal.ts').then(function(m) {
          void m.openBlacklistAddModal({
            kind: 'customer', ref: String(customer.id || ''),
            onDone: function() {
              var body = document.getElementById('customer-drawer-body');
              if (body) _loadCustomerDrawerContent(customer.id, body);
              try { _odswiezKlientowPoMutacji(); } catch (_e) { /* lista jest dodatkiem */ }
            },
          });
        }).catch(function() { showToast('Nie udało się otworzyć okna czarnej listy', 'error'); });
      });
    }
    var mergeBtn = document.getElementById('cd-merge-btn');
    if (mergeBtn) {
      mergeBtn.addEventListener('click', async function() {
        var mergeId = await showPrompt('Scalić klientów?',
          'Podaj ID klienta, który ma zostać scalony z ' + customer.id + '.',
          { label: 'ID klienta do scalenia', required: true });
        if (!mergeId) return;
        SellBaseAPI.mergeCustomers(customer.id, [mergeId]).then(function() {
          showToast('Klienci scaleni', 'success');
          _odswiezKlientowPoMutacji();
          _loadCustomerDrawerContent(customer.id, document.getElementById('customer-drawer-body')!);
        }).catch(function(e: ApiError) { showToast('Błąd: ' + e.message, 'error'); });
      });
    }

    // Address edit buttons (delivery / invoice)
    document.querySelectorAll('.cd-edit-addr-btn').forEach(function(btn) {
      btn.addEventListener('click', function(this: any) {
        var prefix = this.getAttribute('data-prefix') || 'delivery';
        var section = document.getElementById('cd-addr-' + prefix + '-section');
        if (!section) return;
        var title = prefix === 'delivery' ? 'Adres' : 'Dane do faktury';
        section.outerHTML = _buildCustomerAddressSectionEditable(customer, prefix, title);
        if (window.lucide) lucide.createIcons();
        _bindAddressEditFormEvents(customer, prefix);
      });
    });

    // 2026-06-10: "+ Dodaj dane do faktury" (placeholder gdy klient nie ma danych
    // fakturowych) → otwiera ten sam formularz edycji co sekcja faktury.
    var addInvBtn = document.getElementById('cd-add-invoice-btn');
    if (addInvBtn) {
      addInvBtn.addEventListener('click', function() {
        var section = document.getElementById('cd-addr-invoice-section');
        if (!section) return;
        section.outerHTML = _buildCustomerAddressSectionEditable(customer, 'invoice', 'Dane do faktury');
        if (window.lucide) lucide.createIcons();
        _bindAddressEditFormEvents(customer, 'invoice');
      });
    }

    // Delete button (header)
    var delBtn = document.getElementById('cd-delete-btn');
    if (delBtn) {
      delBtn.addEventListener('click', async function() {
        var label = customer.company_name || ((customer.first_name||'') + ' ' + (customer.last_name||'')).trim() || customer.email || customer.id;
        if (!(await showConfirm('Usunąć klienta?', 'Klient „' + label + '" zostanie oznaczony jako usunięty.',
          { danger: true, note: 'Z poziomu panelu operacja jest nieodwracalna.' }))) return;
        (delBtn as any).disabled = true;
        try {
          await SellBaseAPI.deleteCustomer(customer.id);
          showToast('Klient usunięty', 'success');
          closeCustomerDrawer();
          _odswiezKlientowPoMutacji();
        } catch (e: any) {
          showToast('Błąd: ' + (e.message || ''), 'error');
          (delBtn as any).disabled = false;
        }
      });
    }
}

function _bindAddressEditFormEvents(customer: CustomerRow, prefix: string) {
    // Cancel
    document.querySelectorAll('.cd-edit-addr-cancel-btn').forEach(function(btn) {
      if (btn.getAttribute('data-prefix') !== prefix) return;
      btn.addEventListener('click', function() {
        _loadCustomerDrawerContent(customer.id, document.getElementById('customer-drawer-body')!);
      });
    });
    // Save
    document.querySelectorAll('.cd-edit-addr-save-btn').forEach(function(btn) {
      if (btn.getAttribute('data-prefix') !== prefix) return;
      btn.addEventListener('click', async function() {
        var payload: Record<string, string> = {};
        document.querySelectorAll('.cd-addr-input').forEach(function(el: any) {
          var field = el.getAttribute('data-field');
          if (!field) return;
          if (!field.startsWith(prefix + '_') && field !== 'invoice_nip') return;
          payload[field] = (el.value || '').trim();
        });
        if (!payload[prefix + '_country']) payload[prefix + '_country'] = 'PL';
        (btn as any).disabled = true;
        var orig = btn.innerHTML;
        btn.innerHTML = 'Zapisuję...';
        try {
          await SellBaseAPI.updateCustomer(customer.id, payload);
          showToast('Adres zapisany', 'success');
          _odswiezKlientowPoMutacji();
          _loadCustomerDrawerContent(customer.id, document.getElementById('customer-drawer-body')!);
        } catch (e: any) {
          showToast('Błąd: ' + (e.message || ''), 'error');
          (btn as any).disabled = false;
          btn.innerHTML = orig;
        }
      });
    });
}

function _bindEditDataFormEvents(customer: CustomerRow) {
    // Toggle company fields when type changes
    var typeSel = document.getElementById('cd-edit-type');
    if (typeSel) {
      typeSel.addEventListener('change', function() {
        var companyFields = document.getElementById('cd-edit-company-fields');
        if (companyFields) companyFields.style.display = ((this as any).value === 'company') ? 'block' : 'none';
      });
    }

    // Cancel — re-render whole drawer (revert)
    var cancelBtn = document.getElementById('cd-edit-cancel-btn');
    if (cancelBtn) {
      cancelBtn.addEventListener('click', function() {
        _loadCustomerDrawerContent(customer.id, document.getElementById('customer-drawer-body')!);
      });
    }

    // Save — collect, PUT, reload
    var saveBtn: any = document.getElementById('cd-edit-save-btn');
    if (saveBtn) {
      saveBtn.addEventListener('click', async function() {
        var typeVal = ((document.getElementById('cd-edit-type') || {}) as any).value || 'individual';
        var payload: Record<string, string> = {
          type: typeVal,
          first_name: ((document.getElementById('cd-edit-fname') || {}) as any).value || '',
          last_name: ((document.getElementById('cd-edit-lname') || {}) as any).value || '',
          email: ((document.getElementById('cd-edit-email') || {}) as any).value || '',
          phone: ((document.getElementById('cd-edit-phone') || {}) as any).value || '',
          status: ((document.getElementById('cd-edit-status') || {}) as any).value || 'standard',
        };
        if (typeVal === 'company') {
          payload.company_name = ((document.getElementById('cd-edit-company-name') || {}) as any).value || '';
          payload.nip = ((document.getElementById('cd-edit-nip') || {}) as any).value || '';
          if (!payload.company_name.trim()) { showToast('Podaj nazwę firmy', 'error'); return; }
        } else {
          // Czyszczenie firmowych pól przy zmianie na osobę
          payload.company_name = '';
          payload.nip = '';
          if (!payload.first_name.trim() || !payload.last_name.trim()) { showToast('Podaj imię i nazwisko', 'error'); return; }
        }
        if (!payload.email.trim()) { showToast('Podaj email', 'error'); return; }

        (saveBtn as any).disabled = true;
        var origHtml = saveBtn.innerHTML;
        saveBtn.innerHTML = 'Zapisuję...';
        try {
          await SellBaseAPI.updateCustomer(customer.id, payload);
          showToast('Dane zapisane', 'success');
          _odswiezKlientowPoMutacji();
          _loadCustomerDrawerContent(customer.id, document.getElementById('customer-drawer-body')!);
        } catch (e: any) {
          showToast('Błąd: ' + (e.message || ''), 'error');
          (saveBtn as any).disabled = false;
          saveBtn.innerHTML = origHtml;
        }
      });
    }
}

// ── EXPOSE: extracted functions ──
(window as any).SellBaseApp = (window as any).SellBaseApp || {};
window.openCustomerDrawer = openCustomerDrawer; window.SellBaseApp.openCustomerDrawer = openCustomerDrawer;
window.closeCustomerDrawer = closeCustomerDrawer; window.SellBaseApp.closeCustomerDrawer = closeCustomerDrawer;
window._loadCustomerDrawerContent = _loadCustomerDrawerContent; window.SellBaseApp._loadCustomerDrawerContent = _loadCustomerDrawerContent;
window._buildCustomerDataSection = _buildCustomerDataSection; window.SellBaseApp._buildCustomerDataSection = _buildCustomerDataSection;
window._buildCustomerDataSectionEditable = _buildCustomerDataSectionEditable; window.SellBaseApp._buildCustomerDataSectionEditable = _buildCustomerDataSectionEditable;
window._bindEditDataFormEvents = _bindEditDataFormEvents; window.SellBaseApp._bindEditDataFormEvents = _bindEditDataFormEvents;
window._buildCustomerAddressSection = _buildCustomerAddressSection; window.SellBaseApp._buildCustomerAddressSection = _buildCustomerAddressSection;
window._buildCustomerAddressSectionEditable = _buildCustomerAddressSectionEditable; window.SellBaseApp._buildCustomerAddressSectionEditable = _buildCustomerAddressSectionEditable;
window._bindAddressEditFormEvents = _bindAddressEditFormEvents; window.SellBaseApp._bindAddressEditFormEvents = _bindAddressEditFormEvents;
window._buildCustomerOrdersTimeline = _buildCustomerOrdersTimeline; window.SellBaseApp._buildCustomerOrdersTimeline = _buildCustomerOrdersTimeline;
window._buildCustomerOrdersDetailed = _buildCustomerOrdersDetailed; window.SellBaseApp._buildCustomerOrdersDetailed = _buildCustomerOrdersDetailed;
window._buildCustomerNotesSection = _buildCustomerNotesSection; window.SellBaseApp._buildCustomerNotesSection = _buildCustomerNotesSection;
window._loadNewCustomerForm = _loadNewCustomerForm; window.SellBaseApp._loadNewCustomerForm = _loadNewCustomerForm;
window._bindCustomerDrawerEvents = _bindCustomerDrawerEvents; window.SellBaseApp._bindCustomerDrawerEvents = _bindCustomerDrawerEvents;

// ── ES Module exports ──
export {
  _bindAddressEditFormEvents,
  _bindCustomerDrawerEvents,
  _bindEditDataFormEvents,
  _buildCustomerAddressSection,
  _buildCustomerAddressSectionEditable,
  _buildCustomerDataSection,
  _buildCustomerDataSectionEditable,
  _buildCustomerNotesSection,
  _buildCustomerOrdersDetailed,
  _buildCustomerOrdersTimeline,
  _loadCustomerDrawerContent,
  _loadNewCustomerForm,
  closeCustomerDrawer,
  openCustomerDrawer,
};
